import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, GraduationCap, ShieldCheck, Copy, Check, Search, MessageSquareText } from "lucide-react";

const API = "/api";
const LESSON_COUNT = 7;

const shortHash = (h) => (h ? `${h.slice(0, 10)}…${h.slice(-8)}` : "");

const VerdictBadge = ({ verdict }) => {
  const ok = verdict === "ACADEMY_REWARD_VERIFIED_ON_CHAIN";
  return (
    <span
      data-testid="reward-verdict-badge"
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] ${
        ok ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" : "bg-amber-500/10 text-amber-400 border border-amber-500/40"
      }`}
    >
      <ShieldCheck size={13} />
      {ok ? "Verified on chain 9940" : (verdict || "Not yet verified").replaceAll("_", " ")}
    </span>
  );
};

export default function RewardProof() {
  const [token, setToken] = useState(() => sessionStorage.getItem("wallet_token") || "");
  const [loginForm, setLoginForm] = useState({ ledger_uuid: "", phone: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [academy, setAcademy] = useState(null);
  const [copied, setCopied] = useState(false);
  const [proofHash, setProofHash] = useState("");
  const [proof, setProof] = useState(null);
  const [proofError, setProofError] = useState("");
  const [proofBusy, setProofBusy] = useState(false);
  const [honorRoll, setHonorRoll] = useState(null);

  useEffect(() => {
    fetch(`${API}/academy/honor-roll`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setHonorRoll)
      .catch(() => {});
  }, []);

  const loadAcademy = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/wallet/academy`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        sessionStorage.removeItem("wallet_token");
        setToken("");
        return;
      }
      if (res.ok) setAcademy(await res.json());
    } catch {
      /* transient — the page retries on action */
    }
  }, [token]);

  useEffect(() => { loadAcademy(); }, [loadAcademy]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/wallet/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ledger_uuid: loginForm.ledger_uuid.trim(), phone: loginForm.phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data?.detail;
        const msg = typeof d === "string" ? d
          : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
          : (d?.message || d?.code) || "Invalid member credentials";
        throw new Error(msg);
      }
      sessionStorage.setItem("wallet_token", data.token);
      setToken(data.token);
    } catch (err) {
      const offline = err.name === "TypeError" || err.name === "SyntaxError";
      setLoginError(offline ? "Couldn't reach the exchange — it may be mid-restart; retry in a few seconds." : err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const runVerify = async (hash) => {
    setProofBusy(true);
    setProof(null);
    setProofError("");
    try {
      const res = await fetch(`${API}/academy/proof/${encodeURIComponent(hash)}`);
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail;
        throw new Error(typeof d === "string" ? d.replaceAll("_", " ") : d?.message || "Verification failed");
      }
      setProof(data);
    } catch (err) {
      setProofError(err.message);
    } finally {
      setProofBusy(false);
    }
  };

  const verifyProof = (e) => {
    e.preventDefault();
    runVerify(proofHash.trim());
  };

  const verifyGraduate = (tx) => {
    setProofHash(tx);
    runVerify(tx);
    document.getElementById("proof-verify-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const copyTx = async () => {
    if (!academy?.reward_tx) return;
    try {
      await navigator.clipboard.writeText(academy.reward_tx);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  const smsNumber = academy?.learn_sms_number || "+17754022719";

  return (
    <div data-testid="reward-page" className="min-h-screen bg-[#0c0a09] text-stone-200">
      <div className="max-w-3xl mx-auto px-6 py-14 sm:py-20">
        <a href="/" data-testid="reward-back-link"
           className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-amber-500 transition-colors">
          <ArrowLeft size={13} /> bgnzn.org
        </a>

        <p className="mt-10 text-[11px] uppercase tracking-[0.4em] text-amber-500">The BMEX Academy · Chain 9940</p>
        <h1 className="mt-4 text-4xl sm:text-6xl font-black tracking-tight text-stone-100 leading-[0.98]" style={{ fontFamily: "Georgia, serif" }}>
          THE <span className="text-amber-500">$1.00</span> PROOF
        </h1>
        <p className="mt-5 text-stone-400 text-sm sm:text-base leading-relaxed max-w-xl">
          Finish the seven-lesson text message course and the treasury settles a real $1.00 sUSD
          into your wallet — signed inside the hardware module, verifiable by anyone, forever.
          This page is where that proof lives.
        </p>

        {/* ---------------- member academy status ---------------- */}
        <motion.section
          data-testid="academy-status-section"
          className="mt-12 rounded-3xl border border-amber-500/25 bg-stone-900/60 p-6 sm:p-8"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        >
          {!token ? (
            <>
              <div className="flex items-center gap-3 mb-6">
                <GraduationCap size={20} className="text-amber-500" />
                <h2 className="text-stone-100 font-bold text-sm uppercase tracking-[0.2em]">Your course & reward</h2>
              </div>
              <form data-testid="reward-login-form" onSubmit={handleLogin} className="space-y-4">
                <input
                  data-testid="reward-uuid-input"
                  type="text"
                  placeholder="Ledger UUID (e.g. 1803b262-…)"
                  value={loginForm.ledger_uuid}
                  onChange={(e) => setLoginForm({ ...loginForm, ledger_uuid: e.target.value })}
                  className="w-full rounded-xl bg-black/50 border border-stone-700 focus:border-amber-500/70 outline-none px-4 py-3 font-mono text-sm text-stone-100 placeholder:text-stone-600 transition-colors"
                />
                <input
                  data-testid="reward-phone-input"
                  type="tel"
                  placeholder="Registered phone number"
                  value={loginForm.phone}
                  onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                  className="w-full rounded-xl bg-black/50 border border-stone-700 focus:border-amber-500/70 outline-none px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 transition-colors"
                />
                {loginError && <p data-testid="reward-login-error" className="text-red-400 text-xs">{loginError}</p>}
                <button
                  data-testid="reward-login-submit"
                  type="submit"
                  disabled={loginBusy}
                  className="w-full rounded-full bg-amber-500 text-black font-bold text-sm uppercase tracking-wider py-3.5 hover:bg-amber-400 disabled:opacity-50 transition-colors"
                >
                  {loginBusy ? "Checking…" : "Show my proof"}
                </button>
                <p className="text-stone-600 text-xs text-center">Same two fields as your Member Wallet sign-in.</p>
              </form>
            </>
          ) : !academy ? (
            <p className="text-stone-500 text-sm font-mono tracking-widest">LOADING ACADEMY LEDGER…</p>
          ) : (
            <div data-testid="academy-status-card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <GraduationCap size={20} className="text-amber-500" />
                  <h2 className="text-stone-100 font-bold text-sm uppercase tracking-[0.2em]">Your course & reward</h2>
                </div>
                <span data-testid="academy-status-chip" className="font-mono text-[10px] tracking-[0.2em] text-stone-500 uppercase">
                  {academy.status.replaceAll("_", " ")}
                </span>
              </div>

              <div data-testid="academy-progress" className="mb-8">
                <div className="flex items-center gap-2 mb-2" aria-hidden="true">
                  {Array.from({ length: academy.lessons_total || LESSON_COUNT }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 flex-1 rounded-full ${i < academy.lessons_completed ? "bg-amber-500" : "bg-stone-700"}`}
                    />
                  ))}
                </div>
                <p className="text-stone-500 text-xs">
                  {academy.lessons_completed} of {academy.lessons_total} lessons complete
                </p>
              </div>

              {academy.reward_tx ? (
                <div className="rounded-2xl border border-emerald-500/30 bg-black/40 p-5 sm:p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <p className="text-3xl font-black text-stone-100" style={{ fontFamily: "Georgia, serif" }}>
                      ${academy.reward_usd.toFixed(2)} <span className="text-amber-500 text-xl">sUSD</span>
                    </p>
                    <VerdictBadge verdict={academy.chain_state} />
                  </div>
                  <div className="space-y-2.5 font-mono text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-stone-500 uppercase tracking-widest">tx</span>
                      <span className="flex items-center gap-2">
                        <span data-testid="reward-tx-hash" className="text-amber-500">{shortHash(academy.reward_tx)}</span>
                        <button data-testid="reward-copy-tx-btn" onClick={copyTx} aria-label="copy transaction hash"
                                className="text-stone-500 hover:text-amber-500 transition-colors">
                          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                        </button>
                      </span>
                    </div>
                    {academy.on_chain && (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-stone-500 uppercase tracking-widest">block</span>
                          <span data-testid="reward-block" className="text-stone-300">#{academy.on_chain.block}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-stone-500 uppercase tracking-widest">from</span>
                          <span className="text-stone-300" title="treasury authority">{shortHash(academy.on_chain.from_treasury)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-stone-500 uppercase tracking-widest">to</span>
                          <span className="text-stone-300" title="your wallet">{shortHash(academy.on_chain.decoded_transfer?.recipient)}</span>
                        </div>
                      </>
                    )}
                    {academy.reward_claimed_at && (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-stone-500 uppercase tracking-widest">claimed</span>
                        <span className="text-stone-300">{new Date(academy.reward_claimed_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-stone-700 bg-black/40 p-6 text-center">
                  <p className="text-stone-300 text-sm mb-5">
                    {academy.course_complete
                      ? "Course complete — your reward is queued for the next settlement window."
                      : academy.lessons_completed > 0
                        ? "Keep going — the $1.00 sUSD reward settles the moment the final lesson lands."
                        : "Text LEARN from your registered phone to start the seven-lesson course."}
                  </p>
                  <a
                    data-testid="reward-learn-cta"
                    href={`sms:${smsNumber}?&body=LEARN`}
                    className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-black font-bold text-xs uppercase tracking-wider px-6 py-3 hover:bg-amber-400 transition-colors"
                  >
                    <MessageSquareText size={14} /> Text LEARN to {smsNumber}
                  </a>
                </div>
              )}
            </div>
          )}
        </motion.section>

        {/* ---------------- public verification ---------------- */}
        <motion.section
          id="proof-verify-section"
          data-testid="proof-verify-section"
          className="mt-8 rounded-3xl border border-stone-800 bg-stone-900/40 p-6 sm:p-8"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <Search size={18} className="text-amber-500" />
            <h2 className="text-stone-100 font-bold text-sm uppercase tracking-[0.2em]">Verify any reward</h2>
          </div>
          <p className="text-stone-500 text-xs mb-6 leading-relaxed">
            Anyone holding a completion text can check its transaction hash against chain 9940 —
            the proof confirms the treasury signed exactly $1.00 sUSD to the graduate's wallet.
            No phone numbers are ever exposed.
          </p>
          <form data-testid="proof-verify-form" onSubmit={verifyProof} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="proof-hash-input"
              type="text"
              placeholder="0x… transaction hash from the completion text"
              value={proofHash}
              onChange={(e) => setProofHash(e.target.value)}
              className="flex-1 rounded-xl bg-black/50 border border-stone-700 focus:border-amber-500/70 outline-none px-4 py-3 font-mono text-xs text-stone-100 placeholder:text-stone-600 transition-colors"
            />
            <button
              data-testid="proof-verify-btn"
              type="submit"
              disabled={proofBusy || !proofHash.trim()}
              className="rounded-full border border-amber-500/60 text-amber-500 font-bold text-xs uppercase tracking-wider px-6 py-3 hover:bg-amber-500/10 disabled:opacity-50 transition-colors"
            >
              {proofBusy ? "Verifying…" : "Verify on-chain"}
            </button>
          </form>
          {proofError && <p data-testid="proof-error" className="mt-4 text-red-400 text-xs">{proofError}</p>}
          {proof && (
            <div data-testid="proof-result" className="mt-6 rounded-2xl border border-stone-700 bg-black/40 p-5 space-y-3">
              <VerdictBadge verdict={proof.verdict} />
              <div className="font-mono text-xs space-y-2 pt-1">
                <div className="flex justify-between gap-3"><span className="text-stone-500 uppercase tracking-widest">status</span><span className="text-stone-300">{proof.status}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500 uppercase tracking-widest">block</span><span className="text-stone-300">#{proof.block}</span></div>
                {proof.decoded_transfer && (
                  <div className="flex justify-between gap-3"><span className="text-stone-500 uppercase tracking-widest">amount</span><span className="text-amber-500">${proof.decoded_transfer.amount_usd.toFixed(2)} sUSD</span></div>
                )}
                <div className="flex justify-between gap-3"><span className="text-stone-500 uppercase tracking-widest">treasury</span><span className="text-stone-300">{shortHash(proof.from_treasury)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500 uppercase tracking-widest">academy ledger</span><span className="text-stone-300">{(proof.academy_ledger || "").replaceAll("_", " ")}</span></div>
              </div>
            </div>
          )}
        </motion.section>

        {/* ---------------- graduate honor roll ---------------- */}
        <motion.section
          data-testid="honor-roll-section"
          className="mt-8 rounded-3xl border border-stone-800 bg-stone-900/40 p-6 sm:p-8"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <GraduationCap size={18} className="text-amber-500" />
              <h2 className="text-stone-100 font-bold text-sm uppercase tracking-[0.2em]">Graduate Honor Roll</h2>
            </div>
            {honorRoll && (
              <span data-testid="honor-roll-summary" className="font-mono text-[10px] tracking-[0.2em] text-amber-500 uppercase">
                {honorRoll.graduates} graduate{honorRoll.graduates === 1 ? "" : "s"} · ${honorRoll.total_awarded_usd.toFixed(2)} sUSD awarded
              </span>
            )}
          </div>
          <p className="text-stone-500 text-xs mb-6 leading-relaxed">
            Every name below finished the seven-lesson course and collected the $1.00 sUSD reward —
            tap any graduate to independently verify their transaction on chain 9940.
          </p>
          {!honorRoll ? (
            <p className="text-stone-600 text-xs font-mono tracking-widest">LOADING HONOR ROLL…</p>
          ) : honorRoll.entries.length === 0 ? (
            <p data-testid="honor-roll-empty" className="text-stone-500 text-sm">
              The first graduation is one text away — send LEARN to {smsNumber} to claim the top spot.
            </p>
          ) : (
            <div className="divide-y divide-stone-800">
              {honorRoll.entries.map((entry, i) => (
                <div key={entry.reward_tx} data-testid={`honor-roll-entry-${i + 1}`}
                     className="flex items-center justify-between gap-3 py-3.5">
                  <div className="min-w-0">
                    <p className="text-stone-100 font-semibold text-sm">{entry.graduate}</p>
                    <p className="text-stone-500 text-xs font-mono mt-0.5 truncate">
                      {entry.claimed_at ? new Date(entry.claimed_at).toLocaleDateString() : ""}
                      {entry.reward_block ? ` · block #${entry.reward_block}` : ""} · {shortHash(entry.reward_tx)}
                    </p>
                  </div>
                  <button
                    data-testid={`honor-roll-verify-${i + 1}`}
                    onClick={() => verifyGraduate(entry.reward_tx)}
                    className="flex-none rounded-full border border-amber-500/50 text-amber-500 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 hover:bg-amber-500/10 transition-colors"
                  >
                    Verify
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.section>

        <p className="mt-10 text-center text-stone-600 text-xs font-mono tracking-[0.3em]">
          EVERY REWARD IS A REAL TRANSACTION · SEALED IN HARDWARE · CHAIN 9940
        </p>
      </div>
    </div>
  );
}
